/**
 * Enquiry block behaviour.
 *
 * A real asset rather than a `{% javascript %}` tag in the block, because that
 * tag is only compiled for sections — a theme block's copy is dropped silently.
 * It cost this block its whole modal: the element was never upgraded, so the
 * trigger did nothing and `compiled_assets/scripts.js` held no trace of it.
 *
 * Loaded with `defer` from the block, once per instance. The browser fetches it
 * once and the registration guard at the bottom makes a repeat harmless.
 */
(function () {

  /**
   * Opens the enquiry form in a modal, and keeps its product reference honest.
   *
   * showModal() brings Escape, the focus trap, focus return to the trigger and an
   * inert page behind it. None of that is reimplemented here.
   */
  class ProductAsk extends HTMLElement {
    connectedCallback() {
      // Re-query, but keep the old reference if the modal has already been hoisted
      // out to the body — at that point it is no longer a descendant of this
      // element, so a plain `querySelector` here would come back empty and every
      // later call would quietly do nothing.
      this.modal = this.querySelector('[data-pdp-ask-modal]') || this.modal;
      this.field = this.modal ? this.modal.querySelector('[data-pdp-ask-reference]') : null;

      if (!this.bound) {
        this.bound = true;
        this.onClick = this.onClick.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onVariant = this.onVariant.bind(this);
        this.onPageShow = this.onPageShow.bind(this);
      }

      this.addEventListener('click', this.onClick);
      document.addEventListener('pdp:variant', this.onVariant);
      window.addEventListener('pageshow', this.onPageShow);
      if (this.modal) this.modal.addEventListener('close', this.onClose);

      // Shopify answers a contact form by reloading the page, which would leave the
      // result rendered inside a closed modal — the shopper would send a question
      // and see nothing happen at all. If the panel came back carrying an outcome,
      // it is reopened so they can read it.
      if (this.modal && this.modal.querySelector('.pdp-ask__note')) {
        this.open();
        this.forgetPostedFlag();
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('pdp:variant', this.onVariant);
      window.removeEventListener('pageshow', this.onPageShow);
      if (this.modal) this.modal.removeEventListener('close', this.onClose);
      // A hoisted modal has no parent block any more, so it has to be cleared up
      // here or an editor re-render would stack orphans on the body.
      if (this.modal && this.modal.parentElement === document.body) this.modal.remove();
      this.unlock();
    }

    onClick(event) {
      if (!event.target.closest('[data-pdp-ask-open]')) return;
      this.open();
    }

    open() {
      const modal = this.modal;
      if (!modal || typeof modal.showModal !== 'function') return;

      // A dialog that has been re-parented — which a theme-editor section reload
      // does routinely — keeps its `open` attribute but loses the top layer, and
      // showModal() throws outright on a dialog that is already open. Closing first
      // is the only way back to a real modal in either case.
      if (modal.open) modal.close();

      // Hoisted to the body before it opens, and left there.
      //
      // The block renders inside a tab: a `details` element, inside a panel, inside
      // Shopify's own block wrapper. Any ancestor along that chain with `overflow`,
      // `transform`, `filter` or `contain` becomes the containing block for a fixed
      // element and clips it the moment the dialog loses the top layer — and a
      // clipped modal is one you can see but cannot reach. Rather than audit a
      // chain the merchant can change from the editor, the dialog is moved
      // somewhere with no ancestors at all. `disconnectedCallback` takes it away
      // again, so an editor re-render never leaves one behind.
      if (modal.parentElement !== document.body) document.body.appendChild(modal);

      this.lock();
      modal.showModal();

      // `showModal()` lands focus on the first focusable thing, which is the close
      // button — the shopper opened this to write, so put them in the first field.
      // `preventScroll` because focusing inside a fresh top-layer box otherwise
      // jumps the page behind it.
      const first = modal.querySelector('input:not([type="hidden"]):not([readonly]), textarea');
      if (first) first.focus({ preventScroll: true });
    }

    /**
     * Takes `contact_posted=true` back out of the address bar.
     *
     * Shopify answers the form with a redirect that carries the outcome in the URL,
     * and the URL is what a reload replays — so the thank-you came back every time
     * the shopper refreshed, for a question they sent once. The flag has done its
     * job by the time this runs: the note is already on screen. `replaceState` so
     * the Back button doesn't collect a trail of near-identical entries either.
     *
     * The hash goes too. It is the form's own `action` anchor, not something the
     * shopper navigated to.
     */
    forgetPostedFlag() {
      const url = new URL(window.location.href);
      if (!url.searchParams.has('contact_posted')) return;

      url.searchParams.delete('contact_posted');
      if (url.hash.slice(1).indexOf('ProductAsk-') === 0) url.hash = '';
      window.history.replaceState({}, '', url.toString());
    }

    /** Runs for every way out: the close button, Escape, close(). */
    onClose() {
      this.unlock();
    }

    /**
     * The back/forward cache restores the document as it stood, `open` attribute and
     * all, and a restored modal is no longer in the top layer — it renders in normal
     * flow with no backdrop, which reads as a broken page. Reopening it properly is
     * the only thing that state is good for.
     */
    onPageShow(event) {
      // Restored from the back/forward cache: the browser hands back the document
      // exactly as it stood, which includes whatever was half-typed in the box. A
      // shopper coming back to the page did not ask to resume a question, and one
      // they already sent should certainly not still be sitting there.
      if (event && event.persisted) {
        const form = this.modal && this.modal.querySelector('form[action]');
        if (form) form.reset();
      }

      if (this.modal && this.modal.open) {
        this.modal.close();
        this.open();
      }
    }

    lock() {
      const root = document.documentElement;
      root.dataset.pdpAskOpen = String(Number(root.dataset.pdpAskOpen || 0) + 1);
      root.classList.add('pdp-ask-lock');
      this.locked = true;
    }

    unlock() {
      if (!this.locked) return;
      this.locked = false;
      const root = document.documentElement;
      const open = Math.max(0, Number(root.dataset.pdpAskOpen || 1) - 1);
      if (open === 0) {
        delete root.dataset.pdpAskOpen;
        root.classList.remove('pdp-ask-lock');
      } else {
        root.dataset.pdpAskOpen = String(open);
      }
    }

    /**
     * The product section swaps its own markup through the Section Rendering API and
     * fires `pdp:variant` on the way out. This form lives in a different section, so
     * that event is the only way it can hear about a colour change — and if it never
     * comes, the value Liquid rendered is still right for the variant in the URL.
     */
    onVariant(event) {
      if (!this.field) return;
      const detail = event.detail || {};
      const reference = detail.sku || detail.variantTitle;
      if (!reference) return;

      const title = this.field.dataset.productTitle || '';
      this.field.value = title ? title + ' \u2014 ' + reference : reference;
    }
  }

  if (!customElements.get('product-ask')) {
    customElements.define('product-ask', ProductAsk);
  }
})();
